/* eslint-disable @typescript-eslint/indent */
/* eslint-disable indent */
import { Dispatch } from 'react';
import { notification } from 'antd';

import { SIGN_IN_PATH } from '../../../components/routing/paths.constants';
import { IAction } from '../root/i-action';
import { IApiService } from '../../services/api-service';
import { IState } from '../root/state.interface';
import { SignInRequestDTO, ISignInResponse } from './dto/sign-in.dto';
import { UserActionType } from './user.enum';
import { ILocalStorageService } from '../../services/local-storage-service';
import {
  ACCESS_TOKEN_KEY, ACCESS_TOKEN_EXPIRY_KEY, USER_ID, REFRESH_TOKEN_KEY, USER_PERMISSIONS,
} from './user.constants';
import { ForgotPasswordDTO } from './dto/forgot-password.dto';
import { RefreshTokenDTO } from './dto/refresh-token.dto';
import { EditUserRequestDTO, IEditUserResponse } from './dto/edit-user.dto';
import { IRole } from './i-role';
import { CreateUserRequestDTO, ICreateUserResponse } from './dto/create-user.dto';
import { ResetPasswordDTO } from './dto/reset-password.dto';
import { ChangePasswordDTO } from './dto/change-password.dto';
import { IUser } from './i-user';
import createHistory from '../../../utils/create-history';
import { CreatePasswordRequestDTO, ICreatePasswordResponse } from './dto/create-password.dto';
import { IData } from '../../../utils/create-dataParser/dataParser.util';
import { parseData } from '../../../utils/create-dataParser';
import { HelpActionType } from '../help/help.enum';
import { IDevice } from '../devices/i-devices';
import { IPermission } from './i-permission';

export interface IUserActions {
  signIn: (data: SignInRequestDTO) => Promise<boolean>;
  refreshToken: () => Promise<void>;
  forgotPassword: (data: ForgotPasswordDTO) => Promise<void>;
  forgotUsername: (email: string) => Promise<boolean>;
  resetPassword: (data: ResetPasswordDTO) => Promise<void>;
  changePassword: (data: ChangePasswordDTO) => Promise<void>;
  getCurrentUser: (id: number) => Promise<void>;
  editCurrentUser: (id: number, data: EditUserRequestDTO) => Promise<void>;
  getPickedUser: (id: number) => Promise<void>;
  editPickedUser: (id: number, data: EditUserRequestDTO) => Promise<void>;
  createUser: (data: CreateUserRequestDTO) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
  getUsersList: () => Promise<void>;
  getRolesList: () => Promise<void>;
  logout: (data: RefreshTokenDTO) => Promise<void>;
  getAboutAPI: () => Promise<{[key: string]: {[key: string]: string}}| { [key: string]: string; }>;
  getTimeZones: () => Promise<void>;
  getHelp: (url: string | undefined) => Promise<void>;
  toggleHelp: () => void;
  changeForgottenPassword: (otp: string, newPassword: string) => Promise<boolean>;
  getApiKey: (id: number | undefined) => Promise<IDevice>;
  getDeviceOts: (id: number | undefined) => Promise<Blob>;
  getDeviceOtsNumber: (id: number | undefined) => Promise<number>;
  getPermission: () => Promise<void>;
}

export class UserActions implements IUserActions {
  private readonly state: IState;

  private readonly apiService: IApiService;

  protected readonly dispatch: Dispatch<IAction>;

  private readonly localStorageService: ILocalStorageService;

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
    localStorageService: ILocalStorageService,
  ) {
    this.state = state;
    this.apiService = apiService;
    this.dispatch = dispatch;
    this.localStorageService = localStorageService;
  }

  public getAboutAPI = async (): Promise<{[key: string]: {[key: string]: string}} |
   { [key: string]: string; }> => {
    const userManager = await
      this.apiService.get<{[key: string]: {[key: string]: string}}>({
      url: '/user-manager/about',
    });
    const reportManager = await
      this.apiService.get<{[key: string]: {[key: string]: string}}>({
      url: '/report-server/about',
    });
    const facilityManager = await
      this.apiService.get<{[key: string]: {[key: string]: string}}>({
      url: '/facility-manager/about',
    });
    const assetManager = await
      this.apiService.get<{[key: string]: {[key: string]: string}}>({
      url: '/asset-manager/about',
    });
    const httpIngestor = await
      this.apiService.get<{[key: string]: {[key: string]: string}}>({
      url: '/http-ingestor/about',
    });
    const platformManager = await
      this.apiService.get<{[key: string]: {[key: string]: string}}>({
      url: '/platform-manager/about',
    });

    const payload = {
      user: userManager.version,
      report: reportManager.version,
      facility: facilityManager.version,
      asset: assetManager.version,
      httpIngestor: httpIngestor.version,
      platformManager: platformManager.version,
    };
    return payload;
  };

  public signIn = async (data: SignInRequestDTO): Promise<boolean> => {
    try {
      this.dispatch({ type: UserActionType.AUTH_REQUEST, payload: null });
      const payload = await this.apiService.post<SignInRequestDTO, ISignInResponse>({
        url: 'user-manager/api/login',
        data,
      });
      this.localStorageService.set(ACCESS_TOKEN_KEY, payload.accessToken);
      this.localStorageService.set(ACCESS_TOKEN_EXPIRY_KEY, payload.accessTokenExpiry);
      this.localStorageService.set(REFRESH_TOKEN_KEY, payload.refreshToken);
      this.localStorageService.set(USER_ID, payload.user_id);
      this.dispatch({ type: UserActionType.AUTH_SUCCESS, payload });
      return true;
    } catch (error) {
      this.dispatch({ type: UserActionType.AUTH_FAIL, payload: error });
      notification.error({
        message: 'Login failed',
        description: 'You have entered an invalid username or password. Please try again.',
        placement: 'topRight',
        duration: 5,
      });
      return false;
    }
  };

  public getPermission = async () : Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.PERMISSION_REQUEST, payload: null });
      const payload = await this.apiService.get<IPermission[]>({
        url: 'user-manager/api/permissions',
      });
      this.localStorageService.set(USER_PERMISSIONS, payload);
      this.dispatch({ type: UserActionType.PERMISSION_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.PERMISSION_FAIL, payload: error });
    }
  };

  public refreshToken = async (): Promise<void> => {
    // try {
    //   this.dispatch({ type: UserActionType.GET_REFRESH_TOKEN_REQUEST, payload: null });
    //   const payload = await this.apiService.post<RefreshTokenDTO, ISignInResponse>({
    //     url: 'user-manager/api/refresh',
    //     silent: true,
    //     data: {
    //       refreshToken: this.localStorageService.get(REFRESH_TOKEN_KEY) || '',
    //     },
    //   });
    //   this.localStorageService.set(ACCESS_TOKEN_KEY, payload.accessToken);
    //   this.localStorageService.set(ACCESS_TOKEN_EXPIRY_KEY, payload.accessTokenExpiry);
    //   this.localStorageService.set(REFRESH_TOKEN_KEY, payload.refreshToken);
    //   this.dispatch({ type: UserActionType.GET_REFRESH_TOKEN_SUCCESS, payload });
    // } catch (error) {
    //   this.dispatch({ type: UserActionType.GET_REFRESH_TOKEN_FAIL, payload: error });
    // }
  };

  public forgotPassword = async (data: ForgotPasswordDTO): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.FORGOT_PASSWORD_REQUEST, payload: null });
      await this.apiService.post<ForgotPasswordDTO, null>({
        url: '/user-manager/api/forgotPassword',
        data,
      });
      this.dispatch({ type: UserActionType.FORGOT_PASSWORD_SUCCESS, payload: null });
    } catch (err) {
      this.dispatch({ type: UserActionType.FORGOT_PASSWORD_FAIL, payload: null });
    }
  };

  public forgotUsername = async (email: string): Promise<boolean> => {
    try {
      // eslint-disable-next-line
      await this.apiService.post<any, null>({
        url: '/user-manager/api/forgotUsername',
        data: { email },
        silent: true,
      });
      return true;
    } catch (err) {
      return false;
    }
  };

  public resetPassword = async (data: ResetPasswordDTO): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.RESET_PASS_REQUEST, payload: null });
      await this.apiService.post<ResetPasswordDTO, null>({
        url: '/user-manager/api/resetPassword',
        data,
      });
      this.dispatch({ type: UserActionType.RESET_PASS_SUCCESS, payload: null });
      createHistory.push(SIGN_IN_PATH);
    } catch (err) {
      this.dispatch({ type: UserActionType.RESET_PASS_FAIL, payload: null });
    }
  };

  public changePassword = async (data: ChangePasswordDTO): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.CHANGE_PASS_REQUEST, payload: null });
      await this.apiService.post<ChangePasswordDTO, null>({
        url: '/user-manager/api/changePassword',
        data,
      });
      this.dispatch({ type: UserActionType.CHANGE_PASS_SUCCESS, payload: null });
      notification.success({ message: 'Saved!' });
    } catch (e) {
      notification.error({ message: 'Failed!' });
    }
  };

  public getCurrentUser = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.GET_CURRENT_USER_REQUEST, payload: id });
      const user = await this.apiService.get<IUser>({
        url: `/user-manager/crud/user/${id}`,
      });
      const roles = await this.apiService.get<IRole[]>({
        url: '/user-manager/crud/roles',
      });
      const roleOfUser = roles.find((role) => user.role_id === role.id);
      const payload = { ...user, role: roleOfUser?.name, role_code: roleOfUser?.code };
      this.dispatch({ type: UserActionType.GET_CURRENT_USER_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.GET_CURRENT_USER_FAIL, payload: error });
    }
  };

  public editCurrentUser = async (id: number, data: EditUserRequestDTO): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.EDIT_CURRENT_USER_REQUEST, payload: data });
      const payload = await this.apiService.put<EditUserRequestDTO, IEditUserResponse>({
        url: `/user-manager/crud/user/${id}`,
        data,
      });
      this.dispatch({ type: UserActionType.EDIT_CURRENT_USER_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.EDIT_CURRENT_USER_FAIL, payload: error });
    }
  };

  public getPickedUser = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.GET_PICKED_USER_REQUEST, payload: id });
      const user = await this.apiService.get<IUser>({
        url: `/user-manager/crud/user/${id}`,
      });
      const roles = await this.apiService.get<IRole[]>({
        url: '/user-manager/crud/roles',
      });
      const roleOfUser = roles.find((role) => user.role_id === role.id);
      const payload = { ...user, role: roleOfUser?.name, role_code: roleOfUser?.code };
      this.dispatch({ type: UserActionType.GET_PICKED_USER_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.GET_PICKED_USER_FAIL, payload: error });
    }
  };

  public editPickedUser = async (id: number, data: EditUserRequestDTO): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.EDIT_PICKED_USER_REQUEST, payload: data });
      const payload = await this.apiService.put<EditUserRequestDTO, IEditUserResponse>({
        url: `/user-manager/crud/user/${id}`,
        data,
      });
      this.dispatch({ type: UserActionType.EDIT_PICKED_USER_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.EDIT_PICKED_USER_FAIL, payload: error });
    }
  };

  public createUser = async (data: CreateUserRequestDTO): Promise<void> => {
    try {
      if (data.password) {
      this.dispatch({ type: UserActionType.CREATE_USER_REQUEST, payload: null });
      const user = await this.apiService.post<CreateUserRequestDTO, ICreateUserResponse>({
        url: '/user-manager/crud/users',
        data: {
          role_id: data.role_id,
          tenant: data.tenant,
          name: data.name,
          firstname: data.firstname,
          lastname: data.lastname,
          email: data.email,
          timeZone: data.timeZone,
        },
      });
        const password = await
          this.apiService.post<CreatePasswordRequestDTO, ICreatePasswordResponse>({
          url: '/user-manager/api/initializePassword',
          data: {
            user_id: user.id,
            password: data.password,
          },
        });
      const payload = { ...user, password };
      this.dispatch({ type: UserActionType.CREATE_USER_SUCCESS, payload });
    }
    } catch (error) {
      this.dispatch({ type: UserActionType.CREATE_USER_FAIL, payload: error });
    }
  };

  public deleteUser = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.DELETE_USER_REQUEST, payload: null });
      const payload = await this.apiService.delete<null>({
        url: `/user-manager/crud/user/${id}`,
      });
      this.dispatch({ type: UserActionType.DELETE_USER_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.DELETE_USER_FAIL, payload: error });
    }
  };

  public getUsersList = async (): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.GET_USERS_LIST_REQUEST, payload: null });
      const data = await this.apiService.get<IUser[]>({
        url: '/user-manager/api/users?limit=10000',
      });
      const users:IData = parseData(data);

      const roles = await this.apiService.get<IRole[]>({
        url: '/user-manager/crud/roles',
      });
      const payload = users.map((user) => {
        const roleOfUser = roles.find((role) => role.id === user.role_id);
        return { ...user, role: roleOfUser?.name };
      });
      this.dispatch({ type: UserActionType.GET_USERS_LIST_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.GET_USERS_LIST_FAIL, payload: error });
    }
  };

  public getTimeZones = async (): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.GET_TIMEZONES_REQUEST, payload: null });
      const data = await this.apiService.get<string[]>({
        url: '/user-manager/api/timeZones',
      });
      this.dispatch({ type: UserActionType.GET_TIMEZONES_SUCCESS, payload: data });
    } catch (e) {
      this.dispatch({ type: UserActionType.GET_TIMEZONES_FAIL, payload: e });
    }
  };

  public getRolesList = async (): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.GET_ROLES_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<IRole[]>({
        url: '/user-manager/crud/roles',
      });
      this.dispatch({ type: UserActionType.GET_ROLES_LIST_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: UserActionType.GET_ROLES_LIST_FAIL, payload: error });
    }
  };

  public logout = async (data: RefreshTokenDTO): Promise<void> => {
    try {
      this.dispatch({ type: UserActionType.LOGOUT_REQUEST, payload: null });
      const payload = await this.apiService.post<RefreshTokenDTO, null>({
        url: '/user-manager/api/logout',
        data,
      });
      this.localStorageService.remove(ACCESS_TOKEN_KEY);
      this.localStorageService.remove(ACCESS_TOKEN_EXPIRY_KEY);
      this.localStorageService.remove(USER_ID);
      this.localStorageService.remove(USER_PERMISSIONS);
      this.dispatch({ type: UserActionType.LOGOUT_SUCCESS, payload });
      window.location.href = SIGN_IN_PATH;
      // createHistory.push(SIGN_IN_PATH);
    } catch (error) {
      this.dispatch({ type: UserActionType.LOGOUT_FAIL, payload: error });
    }
  };

  public getHelp = async (url: string | undefined): Promise<void> => {
    try {
      if (url) {
        this.dispatch({ type: HelpActionType.GET_HELP_REQUEST, payload: null });
        this.dispatch({
          type: HelpActionType.GET_HELP_SUCCESS,
          payload: { url },
        });
      }
    } catch (error) {
      this.dispatch({ type: HelpActionType.GET_HELP_FAIL, payload: error });
    }
  };

  public toggleHelp = (): void => {
    this.dispatch({ type: HelpActionType.TOGGLE_HELP, payload: null });
  };

  public changeForgottenPassword = async (otp: string, password: string): Promise<boolean> => {
    try {
      this.dispatch({ type: UserActionType.CHANGE_FORGOTTEN_PASSWORD_REQUEST, payload: null });
      // eslint-disable-next-line
      await this.apiService.post<any, any>({
        url: '/user-manager/api/resetPassword',
        data: {
          otp,
          password,
        },
        silent: true,
      });
      notification.success({
        message: 'Success.',
        description:
          'Password was changed successfully',
      });
      this.dispatch({ type: UserActionType.CHANGE_FORGOTTEN_PASSWORD_SUCCESS, payload: null });
      return true;
    } catch (error) {
      this.dispatch({ type: UserActionType.CHANGE_FORGOTTEN_PASSWORD_FAIL, payload: error });
      return false;
    }
  };

  public getApiKey = async (id: number | undefined): Promise<IDevice> => {
    const payload = await
      this.apiService.get<IDevice>({
      url: `/user-manager/api/apikey/${id}`,
    });
    return payload;
  };

  public getDeviceOts = async (id: number | undefined): Promise<Blob> => {
    const payload = await
      this.apiService.get<Blob>({
      url: `/user-manager/api/device/qrcode?text=${id}`, responseType: 'blob',
    });
    return payload;
  };

  public getDeviceOtsNumber = async (id: number | undefined): Promise<number> => {
    const payload = await
      this.apiService.get<number>({
        url: `/user-manager/api/device/ots/${id}`,
      });
      return payload;
  };
}
